<script>
    import { channels, subscribeToChannel } from '$lib/DB';
    import { onDestroy } from 'svelte';
    import { toast } from '@repo/stores/Messaging';
    import { notificationTypes } from '@repo/config/Global';
    import { goto } from '$app/navigation';
    import { focusNextElement, focusOnFirst } from '@repo/config/Utils';

    let { markNotificationAsRead } = $props();

    let notificationDisplay = $state(false);
    let notificationWatcher = null;
    let notifications = $state([]);
    let unreadCount = $derived.by(() => {
        return notifications.filter(notification => !notification.read).length;
    });
    let notificationsContainer = $state();

    notificationWatcher = subscribeToChannel(channels.NOTIFICATIONS, {}, sn => {
        const docChanges = sn.docChanges();
        if (docChanges.length === 0) {
            return;
        }

        let _notifications = $state.snapshot(notifications);

        docChanges.forEach(doc => {
            if (doc.type === 'removed') {
                _notifications = _notifications.filter(notification => notification.id !== doc.doc.id);
            } else if (doc.type === 'added' || doc.type === 'modified') {
                const existingNotification = _notifications.find(notification => notification.id === doc.doc.id);
                if (existingNotification) {
                    Object.assign(existingNotification, doc.doc.data());
                } else {
                    _notifications.push({
                        id: doc.doc.id,
                        ...doc.doc.data(),
                    });
                }
            }
        });
        notifications = _notifications.sort((a, b) => {
            return b.createdAt.seconds - a.createdAt.seconds;
        });
    });

    onDestroy(() => {
        [notificationWatcher].forEach(s => {try {s();} catch (err) {console.error(err);}});
    });

    function getNotificationDisplayDateTime(notification) {
        const createdAt = new Date(notification.createdAt.seconds * 1000);

        const day = String(createdAt.getDate()).padStart(2, '0');
        const month = createdAt.toLocaleString('default', { month: 'long' });
        const year = createdAt.getFullYear();

        let hours = createdAt.getHours();
        const minutes = String(createdAt.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const formattedHours = String(hours).padStart(2, '0');

        return `${day} ${month} ${year} ${formattedHours}:${minutes} ${ampm}`;
    }

    async function markAsRead(notification) {
        if (notification.read) {
            return;
        }

        notification.read = true;

        if (!markNotificationAsRead) {
            return;
        }

        try {
            await markNotificationAsRead(notification.id);
        } catch (err) {
            console.error('Error marking notification as read', err);

            notification.read = false;

            toast.set({
                type: notificationTypes.ERROR,
                message: 'Error marking notification as read',
                closeTime: 5000,
            });
        }
    }

    function handleClick(notification) {
        if (!notification?.link) {
            return;
        }

        const link = notification.link;
        if (link.startsWith('/')) {
            goto(link);
        } else if (link.startsWith('http')) {
            window.open(link, '_blank');
        } else {
            goto(`/${link}`);
        }
    }

    function handleOpenNotifications() {
        notificationDisplay = true;
        setTimeout(() => {
            focusOnFirst('.notifications-container');
        }, 0);
    }

    function handleKeyDown(event) {
        if (event.key === 'Escape') {
            handleClose();
        }
    }

    function handleClose() {
        notificationDisplay = false;

        const notificationIcon = document.querySelector('.notification-icon');
        if (notificationIcon) {
            notificationIcon.focus();
        }
    }

    function handleLossOfFocus(e) {
        setTimeout(() => {
            const focusedElement = document.activeElement;
            if (notificationsContainer && !notificationsContainer.contains(focusedElement)) {
                e.preventDefault();
                e.stopImmediatePropagation();
                notificationDisplay = false;
                focusNextElement('.notifications');
            }
        }, 200);
    }
</script>

<div class="notifications">
    <button onclick={handleOpenNotifications} class="interactive notification-icon" title="Notifications" aria-label="Notifications">
        <i class="material-icons interactive">notifications</i>
        {#if unreadCount > 0}
            <div class="notification-bubble">
                <span class="background"></span>
                <span class="indicator"></span>
            </div>
        {/if}
    </button>
    {#if notificationDisplay}
        <div class="notification-background" onclick={handleClose}></div>
        <div class="notifications-container" bind:this={notificationsContainer} onkeydown={handleKeyDown} onfocusout={handleLossOfFocus}>
            <div class="notification-header">
                <h2>Notifications</h2>
                <button onclick={handleClose} class="interactive notification-close">
                    <i class="material-icons interactive" title="Close notifications">close</i>
                </button>
            </div>
            <ul class="notification-list">
                {#if notifications.length === 0}
                    <li class="notification-item">
                        <div class="notification-container">
                            <span class="notification-content">No notifications</span>
                        </div>
                    </li>
                {/if}
                {#each notifications as notification}
                    <li 
                        class="notification-item"
                    >
                        <button
                            onmouseenter={() => markAsRead(notification)}
                            onclick={() => handleClick(notification)}
                            onfocusout={() => markAsRead(notification)}
                            class="interactive"
                        >
                            <div class="notification-container">
                                <span class="notification-unread">
                                    {#if !notification.read}
                                        <span class="notification-indicator"></span>
                                    {/if}
                                </span>
                                <div>
                                    <span class="notification-content">{notification.content}</span>
                                    <span class="notification-time">{getNotificationDisplayDateTime(notification)}</span>
                                </div>
                                <span class="notification-actions">
                                    <span class="notification-action" title="Mark as read">
                                        <i class="material-icons">open_in_new</i>
                                    </span>
                                </span>
                            </div>
                        </button>
                    </li>
                {/each}
            </ul>
        </div>
    {/if}
</div>


<style>
    .notifications {
        position: relative;
    }
    .notification-icon {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        background: transparent;

        i {
            color: #fff;
        }

        .notification-bubble {
            position: absolute;
            top: 0;
            right: 0;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;

            .background {
                position: absolute;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background-color: #000000;
            }

            .indicator {
                position: absolute;
                width: 80%;
                height: 80%;
                border-radius: 50%;
                background-color: rgb(255, 206, 44);
            }
        }
    }

    .notification-background {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 999;
    }

    .notifications-container {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;

        display: flex;
        width: 100vw;
        max-width: 100vw;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;

        border-radius: 8px;
        background: #1A1A1A;
        box-shadow: 0px 0px 50px 10px rgba(0, 0, 0, 0.70);

        overflow: hidden;

        @media screen and (min-width: 567px) {
            position: absolute;
            max-width: 376px;
            top: 100%;
            right: 0;
            bottom: auto;
            left: auto;
        }

        .notification-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding: 10px;
            background-color: #1A1A1A;
            border-bottom: 1px solid rgb(255, 206, 44);

            h2 {
                font-size: 16px;
                color: rgb(255, 206, 44);
                font-weight: 600;
                text-transform: uppercase;
            }

            .notification-close {
                font-size: 14px;
                color: #fff;
                text-align: center;
                cursor: pointer;
                background: transparent;

                &:hover {
                    color: rgb(255, 206, 44);
                }
            }
        }

        .notification-list {
            color: white;
            max-height: calc(100vh - 50px);
            overflow-y: auto;

            @media screen and (min-width: 567px) {
                max-height: calc(70vh - 50px);
            }

            li {
                padding: 0 10px;

                button {
                    background-color: transparent;
                    text-align: left;
                    color: inherit;
                }

                &:hover {
                    background-color: rgba(255, 255, 255, 0.05);
                }

                &:last-child .notification-container {
                    border-bottom: none;
                }

                .notification-container {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    width: 100%;

                    padding: 10px 0;
                    gap: 10px;
                    cursor: pointer;
                    border-bottom: 1px solid rgba(223, 223, 223, 0.50);

                    .notification-unread {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        overflow: hidden;
                        flex-shrink: 0;

                        .notification-indicator {
                            width: 100%;
                            height: 100%;
                            background-color: rgb(255, 206, 44);
                        }
                    }

                    > div {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        gap: 7px;

                        .notification-content {
                            font-size: 14px;
                            font-weight: 600;
                        }

                        .notification-time {
                            font-size: 12px;
                            color: #DFDFDF;
                        }
                    }

                    .notification-actions {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 20px;
                        height: 20px;
                        border-radius: 50%;
                        overflow: hidden;
                        flex-shrink: 0;

                        .notification-action {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            width: 100%;
                            height: 100%;
                            cursor: pointer;

                            i {
                                font-size: 16px;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
